// =============================================================================
// BP-08-05: Vendor Portal Frontend - VendorReviewsPage
// =============================================================================

import React from 'react';
import { Star, Filter, TrendingUp } from 'lucide-react';
import { VendorHeader } from '@/features/vendor/components/VendorHeader';
import { ReviewCard } from '@/features/vendor/components/ReviewCard';
import { RatingDistributionChart } from '@/features/vendor/components/AnalyticsCharts';
import {
  useVendorReviews,
  useRespondToReview,
  useUpdateReviewResponse,
  useDeleteReviewResponse,
  useReviewStats,
} from '@/features/vendor/hooks/useVendorReviews';
import { useVendorStore } from '@/features/vendor/stores/vendorStore';

export function VendorReviewsPage() {
  const { reviewFilters, setReviewFilters } = useVendorStore();
  const { data, isLoading, fetchNextPage, hasNextPage } = useVendorReviews(reviewFilters);
  const { data: stats } = useReviewStats();
  const respondToReview = useRespondToReview();
  const updateResponse = useUpdateReviewResponse();
  const deleteResponse = useDeleteReviewResponse();

  const reviews = data?.pages.flatMap((page) => page.items) || [];

  return (
    <div className="min-h-screen">
      <VendorHeader
        title="Reviews"
        subtitle="Manage and respond to customer reviews"
      />

      <div className="p-6">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                <span className="text-sm text-warm-gray">Average Rating</span>
              </div>
              <p className="text-2xl font-display font-semibold text-navy">
                {stats.avgRating.toFixed(1)}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                <span className="text-sm text-warm-gray">Total Reviews</span>
              </div>
              <p className="text-2xl font-display font-semibold text-navy">
                {stats.totalReviews}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-warm-gray">Response Rate</span>
              </div>
              <p className="text-2xl font-display font-semibold text-navy">
                {stats.responseRate}%
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-warm-gray">Pending Responses</span>
              </div>
              <p className="text-2xl font-display font-semibold text-navy">
                {stats.pendingResponses}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Reviews List */}
          <div className="lg:col-span-2 space-y-4">
            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex flex-wrap items-center gap-4">
                <select
                  value={reviewFilters.rating || ''}
                  onChange={(e) =>
                    setReviewFilters({
                      rating: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  className="px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-terracotta/20"
                >
                  <option value="">All Ratings</option>
                  <option value="5">5 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="2">2 Stars</option>
                  <option value="1">1 Star</option>
                </select>

                <select
                  value={reviewFilters.hasResponse === undefined ? '' : String(reviewFilters.hasResponse)}
                  onChange={(e) =>
                    setReviewFilters({
                      hasResponse: e.target.value === '' ? undefined : e.target.value === 'true',
                    })
                  }
                  className="px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-terracotta/20"
                >
                  <option value="">All Reviews</option>
                  <option value="false">Needs Response</option>
                  <option value="true">Responded</option>
                </select>

                <select
                  value={reviewFilters.sortBy || 'newest'}
                  onChange={(e) =>
                    setReviewFilters({ sortBy: e.target.value as 'newest' | 'oldest' | 'highest' | 'lowest' })
                  }
                  className="px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-terracotta/20"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="highest">Highest Rating</option>
                  <option value="lowest">Lowest Rating</option>
                </select>
              </div>
            </div>

            {/* Review Cards */}
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-gray-200" />
                      <div className="space-y-2">
                        <div className="h-4 w-24 bg-gray-200 rounded" />
                        <div className="h-3 w-32 bg-gray-200 rounded" />
                      </div>
                    </div>
                    <div className="h-20 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            ) : reviews.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-navy mb-2">No reviews yet</h3>
                <p className="text-warm-gray">Reviews from customers will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <ReviewCard
                    key={review.id}
                    review={review}
                    onRespond={async (reviewId, response) => {
                      await respondToReview.mutateAsync({ reviewId, response });
                    }}
                    onUpdateResponse={async (reviewId, response) => {
                      await updateResponse.mutateAsync({ reviewId, response });
                    }}
                    onDeleteResponse={async (reviewId) => {
                      if (confirm('Delete your response?')) {
                        await deleteResponse.mutateAsync(reviewId);
                      }
                    }}
                  />
                ))}

                {hasNextPage && (
                  <div className="text-center">
                    <button
                      onClick={() => fetchNextPage()}
                      className="px-6 py-2 text-terracotta font-medium hover:bg-cream-dark rounded-lg transition-colors"
                    >
                      Load More Reviews
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Rating Distribution */}
          <div className="space-y-6">
            {stats && (
              <RatingDistributionChart distribution={stats.ratingDistribution} />
            )}

            {/* Tips Card */}
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
              <h3 className="font-semibold text-blue-800 mb-3">
                Tips for Great Responses
              </h3>
              <ul className="space-y-2 text-sm text-blue-700">
                <li>• Thank the customer for their feedback</li>
                <li>• Address specific concerns mentioned</li>
                <li>• Keep responses professional and friendly</li>
                <li>• Offer solutions for negative experiences</li>
                <li>• Respond within 24-48 hours</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VendorReviewsPage;

